import { ExportOptions } from '../ExportOptionsModal'
import { MarketCustomColumn } from '../../../types/database'
import { ColumnDefinition } from './types'

export const getEnabledColumns = (
  tableType: 'for-sale' | 'sold' | 'removed',
  exportOptions: ExportOptions,
  customColumns: MarketCustomColumn[]
): ColumnDefinition[] => {
  const allColumns: ColumnDefinition[] = [
    { header: 'S/N\nReg', dataKey: 'serial', enabled: exportOptions.columns.serialNumber, preferredWidth: 'medium', minWidth: 40, maxWidth: 55 },
    { header: 'Aircraft\nOwner', dataKey: 'owner', enabled: exportOptions.columns.owner, editable: true, preferredWidth: 'medium', minWidth: 25, maxWidth: 50 },
    { header: 'Aircraft\nAgent', dataKey: 'broker', enabled: exportOptions.columns.broker, editable: true, preferredWidth: 'medium', minWidth: 25, maxWidth: 50 },
    { header: 'YOM\nEIS', dataKey: 'year', enabled: exportOptions.columns.yearMfrDlv, preferredWidth: 'small', minWidth: 35, maxWidth: 42 },
    { header: 'AFTT\nS/ New', dataKey: 'aftt', enabled: exportOptions.columns.aftt, preferredWidth: 'small', minWidth: 18, maxWidth: 28 },
    { header: 'Engine\nProg', dataKey: 'emp', enabled: exportOptions.columns.emp, preferredWidth: 'small', minWidth: 15, maxWidth: 22 },
    { header: 'DMG', dataKey: 'damage', enabled: exportOptions.columns.damage, preferredWidth: 'small', minWidth: 28, maxWidth: 35 },
    { header: 'PAX', dataKey: 'pax', enabled: exportOptions.columns.pax, preferredWidth: 'small', minWidth: 18, maxWidth: 28 },
    { header: 'Paint\nInterior', dataKey: 'paintInterior', enabled: exportOptions.columns.paintInterior, preferredWidth: 'small', minWidth: 22, maxWidth: 32 },
    { header: 'Options &\nAdditional Equipment', dataKey: 'options', enabled: exportOptions.columns.options, preferredWidth: 'large', minWidth: 45, maxWidth: 100 }
  ]

  // Add custom columns
  customColumns.forEach((column) => {
    const columnKey = `custom${column.column_index}` as keyof typeof exportOptions.columns
    const headerWords = column.column_title.trim().split(/\s+/)
    const formattedHeader = headerWords.join('\n')
    const longestWord = Math.max(...headerWords.map(w => w.length))
    const calculatedMinWidth = Math.max(28, longestWord * 4 + 16)
    allColumns.push({
      header: formattedHeader,
      dataKey: `custom${column.column_index}`,
      enabled: exportOptions.columns[columnKey] as boolean,
      preferredWidth: 'medium',
      minWidth: calculatedMinWidth,
      maxWidth: 50
    })
  })

  // Add type-specific columns
  const getAskTakeHeader = (): string => {
    const showAsk = exportOptions.columns.askingPrice
    const showTake = exportOptions.columns.takePrice
    if (showAsk && showTake) return 'Ask Price\nTake Price'
    if (showAsk && !showTake) return 'Ask Price'
    if (!showAsk && showTake) return 'Take Price'
    return 'Ask Price\nTake Price'
  }

  const getAskSoldHeader = (): string => {
    const showAsk = exportOptions.columns.askingPrice
    if (showAsk) return 'Ask Price\nSold Price'
    return 'Sold Price'
  }

  switch (tableType) {
    case 'for-sale':
      allColumns.push(
        { header: 'Date Listed\nDOM', dataKey: 'dateListed', enabled: exportOptions.columns.dateListed, preferredWidth: 'medium', minWidth: 35, maxWidth: 55 },
        { header: getAskTakeHeader(), dataKey: 'askingTake', enabled: exportOptions.columns.askingPrice || exportOptions.columns.takePrice, preferredWidth: 'medium', minWidth: 30, maxWidth: 50 }
      )
      break
    case 'sold':
      allColumns.push(
        { header: 'Listed\nSold', dataKey: 'dates', enabled: exportOptions.columns.dateListed, preferredWidth: 'medium', minWidth: 35, maxWidth: 55 },
        { header: getAskSoldHeader(), dataKey: 'prices', enabled: exportOptions.columns.askingPrice || exportOptions.columns.takePrice, preferredWidth: 'medium', minWidth: 50, maxWidth: 70 }
      )
      break
    case 'removed':
      allColumns.push(
        { header: 'Date\nRemoved', dataKey: 'dateRemoved', enabled: exportOptions.columns.dateListed, preferredWidth: 'medium', minWidth: 35, maxWidth: 55 }
      )
      break
  }

  // Add relative value column (always rightmost)
  allColumns.push({
    header: 'Comparative\nValues',
    dataKey: 'relativeValue',
    enabled: exportOptions.columns.relativeValue,
    preferredWidth: 'small',
    minWidth: 22,
    maxWidth: 35
  })

  return allColumns.filter(col => col.enabled)
}

